import React, { useMemo } from 'react';
import moment, { Moment } from 'moment';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
// Helpers
import { formatCurrencyString } from 'helpers/utils';
import { getTicketSalesSeries, TicketSalesPoint, TicketStats } from './helpers';
// Constants
import { monthDayFormat, shortDateFormat } from 'constants/global';
// Styles
import styles from './DashboardTicketSales.module.scss';

// A lone series takes the palette's default single hue; the section title
// names it, so it needs no legend to tell it from the lines above.
const SERIES_COLOR = '#2a78d6';

type DashboardTicketSalesProps = {
  values: TicketStats;
  counts: TicketStats;
  startDate: Moment;
  endDate: Moment;
  valueName: string;
};

const DashboardTicketSales = ({
  values,
  counts,
  startDate,
  endDate,
  valueName,
}: DashboardTicketSalesProps): JSX.Element => {
  const series = useMemo(
    () => getTicketSalesSeries(values, counts, startDate, endDate),
    [values, counts, startDate, endDate]
  );
  const orders = series.reduce((sum, point) => sum + point.orders, 0);

  if (!orders) {
    return <p className={styles.empty}>No ticket sales in this period.</p>;
  }

  return (
    <div className={styles.root}>
      <p className={styles.caption}>
        {orders.toLocaleString('en-US')} paid ticket orders. Already part of the
        totals above.
      </p>
      <ResponsiveContainer height={200}>
        <AreaChart data={series} margin={{ top: 8, right: 8, left: 8 }}>
          <CartesianGrid stroke="#eef1f6" vertical={false} />
          <XAxis
            dataKey="date"
            stroke="#e4e9f0"
            tick={{ fill: '#667085', fontSize: 12 }}
            tickFormatter={(date: string) =>
              moment(date).format(monthDayFormat)
            }
            minTickGap={24}
          />
          <YAxis
            width={64}
            stroke="#e4e9f0"
            tick={{ fill: '#667085', fontSize: 12 }}
            tickFormatter={(value: number) =>
              formatCurrencyString(value, '$', 0)
            }
          />
          <Tooltip
            cursor={{ stroke: '#98a2b3', strokeWidth: 1 }}
            content={({ active, payload }) => {
              // recharts 2.0 types leave out the row each payload item carries
              const point = (payload?.[0] as { payload?: TicketSalesPoint })
                ?.payload;

              return active && point ? (
                <div className={styles.tooltip}>
                  <p className={styles.tooltipDate}>
                    {moment(point.date).format(shortDateFormat)}
                  </p>
                  <p>
                    {valueName}: {formatCurrencyString(point.value)}
                  </p>
                  <p>Orders: {point.orders}</p>
                </div>
              ) : null;
            }}
          />
          <Area
            type="linear"
            dataKey="value"
            name={valueName}
            stroke={SERIES_COLOR}
            strokeWidth={2}
            fill={SERIES_COLOR}
            fillOpacity={0.12}
            activeDot={{ r: 4, fill: SERIES_COLOR, strokeWidth: 0 }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default DashboardTicketSales;
